import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { recordArticleRead } from '@/lib/usageEvents'
import { format } from 'date-fns'
import { useAuth } from '@/contexts/AuthContext'

interface SearchResult {
  type: 'thread' | 'comment' | 'news'
  id: string
  title?: string
  body: string
  transcript?: string | null
  description?: string | null
  created_at: string
  author_id: string | null
  author_name: string
  group_id?: string
  group_name?: string
  thread_id?: string
  thread_title?: string
  article_file_url?: string | null
  publish_date?: string | null
}

export default function Search() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInputRef = useRef<HTMLInputElement>(null)
  
  const query = searchParams.get('q') || ''
  const groupFilter = searchParams.get('group') || 'all'
  const dateFilter = searchParams.get('date') || 'all'
  const sortBy = searchParams.get('sort') || 'relevance'
  const [searchQuery, setSearchQuery] = useState(query)
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      // Update URL params
      const params = new URLSearchParams()
      if (searchQuery.trim()) {
        params.set('q', searchQuery.trim())
      }
      if (groupFilter !== 'all') params.set('group', groupFilter)
      if (dateFilter !== 'all') params.set('date', dateFilter)
      if (sortBy !== 'relevance') params.set('sort', sortBy)
      setSearchParams(params, { replace: true })
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, groupFilter, dateFilter, sortBy, setSearchParams])

  // Focus search input on mount and handle keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Focus search when '/' is pressed (unless in input/textarea)
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [])

  // Fetch groups for filter dropdown
  const { data: groups } = useQuery({
    queryKey: ['all-groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name')
        .order('name', { ascending: true })

      if (error) throw error
      return data || []
    },
  })

  // Perform full-text search
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', debouncedQuery, groupFilter, dateFilter, sortBy],
    queryFn: async () => {
      if (!debouncedQuery.trim() || debouncedQuery.trim().length < 3) {
        return []
      }

      const results: SearchResult[] = []

      // Calculate date range for filter
      let dateFrom: Date | null = null
      if (dateFilter === 'today') {
        dateFrom = new Date()
        dateFrom.setHours(0, 0, 0, 0)
      } else if (dateFilter === 'week') {
        dateFrom = new Date()
        dateFrom.setDate(dateFrom.getDate() - 7)
      } else if (dateFilter === 'month') {
        dateFrom = new Date()
        dateFrom.setMonth(dateFrom.getMonth() - 1)
      } else if (dateFilter === 'year') {
        dateFrom = new Date()
        dateFrom.setFullYear(dateFrom.getFullYear() - 1)
      }

      // Search threads
      let threadsQuery = supabase
        .from('threads')
        .select(`
          id,
          title,
          body,
          transcript,
          created_at,
          author_id,
          group_id,
          groups!threads_group_id_fkey (
            name
          ),
          profiles!threads_author_id_fkey (
            display_name
          )
        `)
        .eq('is_hidden', false)
        .or(`title.ilike.%${debouncedQuery}%,body.ilike.%${debouncedQuery}%,transcript.ilike.%${debouncedQuery}%`)

      if (groupFilter !== 'all') {
        threadsQuery = threadsQuery.eq('group_id', groupFilter)
      }

      if (dateFrom) {
        threadsQuery = threadsQuery.gte('created_at', dateFrom.toISOString())
      }

      const { data: threads, error: threadsError } = await threadsQuery

      if (threadsError) {
        console.error('Error searching threads:', threadsError)
      } else if (threads) {
        threads.forEach((thread: any) => {
          const group = Array.isArray(thread.groups) ? thread.groups[0] : thread.groups
          const profile = Array.isArray(thread.profiles) ? thread.profiles[0] : thread.profiles
          
          results.push({
            type: 'thread',
            id: thread.id,
            title: thread.title,
            body: thread.body,
            transcript: thread.transcript,
            created_at: thread.created_at,
            author_id: thread.author_id,
            author_name: profile?.display_name || 'Unknown',
            group_id: thread.group_id,
            group_name: group?.name || 'Unknown Group',
          })
        })
      }

      // Search comments
      let commentsQuery = supabase
        .from('comments')
        .select(`
          id,
          body,
          transcript,
          created_at,
          author_id,
          thread_id,
          threads!comments_thread_id_fkey (
            id,
            title,
            group_id,
            groups!threads_group_id_fkey (
              name
            )
          ),
          profiles!comments_author_id_fkey (
            display_name
          )
        `)
        .eq('is_hidden', false)
        .or(`body.ilike.%${debouncedQuery}%,transcript.ilike.%${debouncedQuery}%`)

      if (dateFrom) {
        commentsQuery = commentsQuery.gte('created_at', dateFrom.toISOString())
      }

      const { data: comments, error: commentsError } = await commentsQuery

      if (commentsError) {
        console.error('Error searching comments:', commentsError)
      } else if (comments) {
        comments.forEach((comment: any) => {
          const thread = Array.isArray(comment.threads) ? comment.threads[0] : comment.threads
          const profile = Array.isArray(comment.profiles) ? comment.profiles[0] : comment.profiles
          const group = Array.isArray(thread?.groups) ? thread?.groups[0] : thread?.groups

          // Apply group filter for comments
          if (groupFilter === 'all' || thread?.group_id === groupFilter) {
            results.push({
              type: 'comment',
              id: comment.id,
              body: comment.body,
              transcript: comment.transcript,
              created_at: comment.created_at,
              author_id: comment.author_id,
              author_name: profile?.display_name || 'Unknown',
              group_id: thread?.group_id || '',
              group_name: group?.name || 'Unknown Group',
              thread_id: comment.thread_id,
              thread_title: thread?.title,
            })
          }
        })
      }

      // Search news articles
      let newsQuery = supabase
        .from('news_articles')
        .select('*')
        .eq('is_displayed', true)
        .or(`title.ilike.%${debouncedQuery}%,description.ilike.%${debouncedQuery}%,author.ilike.%${debouncedQuery}%`)

      if (dateFrom) {
        newsQuery = newsQuery.gte('created_at', dateFrom.toISOString())
      }

      const { data: newsArticles, error: newsError } = await newsQuery

      if (newsError) {
        console.error('Error searching news articles:', newsError)
      } else if (newsArticles) {
        const now = new Date()
        newsArticles.forEach((article: any) => {
          if (article.expiry_days != null && article.expiry_days > 0 && article.publish_date) {
            const baseDate = new Date(article.publish_date)
            const expiryDate = new Date(baseDate)
            expiryDate.setDate(expiryDate.getDate() + article.expiry_days)
            if (expiryDate < now) return
          }
          results.push({
            type: 'news',
            id: article.id,
            title: article.title,
            body: article.description || '',
            description: article.description,
            created_at: article.created_at,
            author_id: article.created_by,
            author_name: article.author,
            publish_date: article.publish_date,
            article_file_url: article.article_file_url,
          })
        })
      }

      // Sort results
      if (sortBy === 'date') {
        results.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      } else if (sortBy === 'date-oldest') {
        results.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
      }
      // 'relevance' is default (no sorting, keep as found)

      return results
    },
    enabled: debouncedQuery.trim().length >= 3,
  })

  // Highlight search terms in text
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: 'var(--color-focus)', color: 'var(--text-primary)' }}>
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 
        className="text-3xl font-bold mb-6"
        style={{ color: 'var(--text-primary)' }}
      >
        Search
      </h1>

      {/* Search Input */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search threads, comments, and news articles... (Press '/' to focus)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-3 text-lg border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            aria-label="Search threads, comments, and news articles"
          />
          {(searchQuery.trim().length > 0 || groupFilter !== 'all' || dateFilter !== 'all' || sortBy !== 'relevance') && (
            <button
              onClick={() => {
                setSearchQuery('')
                setSearchParams({}, { replace: true })
              }}
              className="px-6 py-3 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              aria-label="Clear search and filters"
            >
              Clear
            </button>
          )}
        </div>
        {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
          <p 
            className="text-sm mt-2"
            style={{ color: 'var(--text-secondary)' }}
          >
            Enter at least 3 characters to search
          </p>
        )}
      </div>

      {/* Filters */}
      {debouncedQuery.trim().length >= 3 && (
        <div className="mb-6 flex flex-wrap gap-4">
          {/* Group Filter */}
          <div>
            <label 
              htmlFor="group-filter"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Group
            </label>
            <select
              id="group-filter"
              value={groupFilter}
              onChange={(e) => setSearchParams(prev => {
                const params = new URLSearchParams(prev)
                if (e.target.value === 'all') {
                  params.delete('group')
                } else {
                  params.set('group', e.target.value)
                }
                return params
              })}
              className="px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="all">All Groups</option>
              {groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label 
              htmlFor="date-filter"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Date
            </label>
            <select
              id="date-filter"
              value={dateFilter}
              onChange={(e) => setSearchParams(prev => {
                const params = new URLSearchParams(prev)
                if (e.target.value === 'all') {
                  params.delete('date')
                } else {
                  params.set('date', e.target.value)
                }
                return params
              })}
              className="px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="year">Past Year</option>
            </select>
          </div>

          {/* Sort Filter */}
          <div>
            <label 
              htmlFor="sort-filter"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Sort By
            </label>
            <select
              id="sort-filter"
              value={sortBy}
              onChange={(e) => setSearchParams(prev => {
                const params = new URLSearchParams(prev)
                if (e.target.value === 'relevance') {
                  params.delete('sort')
                } else {
                  params.set('sort', e.target.value)
                }
                return params
              })}
              className="px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="relevance">Relevance</option>
              <option value="date">Newest First</option>
              <option value="date-oldest">Oldest First</option>
            </select>
          </div>
        </div>
      )}

      {/* Results */}
      {!debouncedQuery.trim() ? (
        <p style={{ color: 'var(--text-primary)' }}>
          Enter a search query to find threads, comments, and news articles.
        </p>
      ) : debouncedQuery.trim().length < 3 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          Please enter at least 3 characters to search.
        </p>
      ) : isLoading ? (
        <p style={{ color: 'var(--text-primary)' }}>Searching...</p>
      ) : searchResults && searchResults.length > 0 ? (
        <div>
          <p 
            className="mb-4 text-lg font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-4">
            {searchResults.map((result) => (
              <div
                key={`${result.type}-${result.id}`}
                className="p-4 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <div className="mb-2">
                  <span 
                    className="px-2 py-1 text-xs font-semibold rounded"
                    style={{
                      backgroundColor: result.type === 'thread' 
                        ? 'var(--bg-interactive)' 
                        : result.type === 'news'
                        ? 'var(--color-focus)'
                        : 'var(--bg-tertiary)',
                      color: 'var(--text-inverse)',
                    }}
                  >
                    {result.type === 'thread' ? 'Thread' : result.type === 'news' ? 'News Article' : 'Comment'}
                  </span>
                  {result.group_name && (
                    <span 
                      className="ml-2 text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      in {result.group_name}
                    </span>
                  )}
                </div>

                {(result.type === 'thread' || result.type === 'news') && result.title && (
                  <h3 
                    className="text-lg font-bold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {result.type === 'thread' ? (
                      <Link
                        to={`/groups/${result.group_id}`}
                        style={{ color: 'var(--text-link)' }}
                      >
                        {highlightText(result.title, debouncedQuery)}
                      </Link>
                    ) : (
                      highlightText(result.title, debouncedQuery)
                    )}
                  </h3>
                )}

                {result.type === 'comment' && result.thread_title && (
                  <div className="mb-2">
                    <p 
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      Comment on:{' '}
                      <Link
                        to={`/groups/${result.group_id}`}
                        style={{ color: 'var(--text-link)' }}
                      >
                        {result.thread_title}
                      </Link>
                    </p>
                  </div>
                )}

                {(result.body || result.description) && (
                  <p 
                    className="mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {highlightText(result.description || result.body, debouncedQuery)}
                  </p>
                )}

                {result.transcript && (
                  <details className="mb-2">
                    <summary 
                      className="cursor-pointer text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      Show transcript
                    </summary>
                    <p 
                      className="mt-2 text-sm italic"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {highlightText(result.transcript, debouncedQuery)}
                    </p>
                  </details>
                )}

                <div 
                  className="text-sm mt-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <span>By {result.author_name}</span>
                  <span className="ml-4">
                    {result.publish_date 
                      ? format(new Date(result.publish_date), 'dd/MM/yyyy')
                      : format(new Date(result.created_at), 'dd/MM/yyyy, HH:mm')}
                  </span>
                  {result.type === 'news' && result.article_file_url && (
                    <a
                      href={result.article_file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4"
                      style={{ color: 'var(--text-link)' }}
                      onClick={async (e) => {
                        e.preventDefault()
                        await recordArticleRead(supabase, result.id, user?.id ?? null)
                        window.open(result.article_file_url!, '_blank', 'noopener,noreferrer')
                      }}
                    >
                      Read Article →
                    </a>
                  )}
                  {result.type !== 'news' && result.group_id && (
                    <Link
                      to={`/groups/${result.group_id}`}
                      className="ml-4"
                      style={{ color: 'var(--text-link)' }}
                    >
                      View in group →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p style={{ color: 'var(--text-primary)' }}>
          No results found. Try different search terms or filters.
        </p>
      )}
    </div>
  )
}

